import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const NAV = [
  { label: "Coverage", href: "#coverage" },
  { label: "Products", href: "#products" },
  { label: "Calculator", href: "#calculator" },
  { label: "Voices", href: "#voices" },
  { label: "Journal", href: "#journal" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => (e) => {
    e.preventDefault();
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1.2, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-[background,backdrop-filter,border-color] duration-500 ${
          scrolled
            ? "bg-ink/70 backdrop-blur-xl border-b border-white/10"
            : "bg-transparent border-b border-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <a href="#top" onClick={handleNav("#top")} className="flex items-center gap-3 group" data-testid="brand-mark">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className="text-cream">
              <path d="M14 2 L26 8 V16 C26 22 20 26 14 26 C8 26 2 22 2 16 V8 Z" stroke="currentColor" strokeWidth="1.5" fill="none" />
              <circle cx="14" cy="14" r="3" fill="#C4A47C" />
            </svg>
            <span className="font-serif text-cream text-lg tracking-wide">Experior <span className="text-gold">Financial</span></span>
          </a>

          <nav className="hidden md:flex items-center gap-10">
            {NAV.map((n) => (
              <a
                key={n.label}
                href={n.href}
                onClick={handleNav(n.href)}
                className="link-line text-cream/80 hover:text-cream text-[13px] tracking-[0.15em] uppercase font-medium"
                data-testid={`nav-${n.label.toLowerCase()}`}
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <a
              href="#schedule"
              onClick={handleNav("#schedule")}
              className="hidden md:inline-flex items-center gap-2 px-6 py-3 border border-cream/30 text-cream text-[12px] tracking-[0.2em] uppercase hover:bg-cream hover:text-ink transition-colors duration-500"
              data-testid="header-schedule-cta"
            >
              Book a Consultation
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6h8m0 0L6 2m4 4L6 10" stroke="currentColor" strokeWidth="1.2"/></svg>
            </a>
            <button
              className="md:hidden text-cream p-2"
              onClick={() => setOpen(!open)}
              aria-label="Menu"
              data-testid="mobile-menu-toggle"
            >
              <div className="w-6 h-[1px] bg-cream mb-1.5" />
              <div className="w-6 h-[1px] bg-cream" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-ink md:hidden pt-24 px-8"
            data-testid="mobile-menu-panel"
          >
            <nav className="flex flex-col gap-8">
              {NAV.map((n) => (
                <a
                  key={n.label}
                  href={n.href}
                  onClick={handleNav(n.href)}
                  className="font-serif text-cream text-4xl"
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                >
                  {n.label}
                </a>
              ))}
              <a
                href="#schedule"
                onClick={handleNav("#schedule")}
                className="mt-8 inline-block px-8 py-4 bg-gold text-ink text-[12px] tracking-[0.2em] uppercase"
                data-testid="mobile-schedule-cta"
              >
                Book a Consultation
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
